import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { Link, Title, useGetIdentity, useNotify } from 'react-admin';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
    aiModerationConfigProxy,
    AiModerationConfig,
    AiModerationConfigHistoryItem,
    SetAiModerationConfigRequest
} from './apiGateway';

const cardStyle = {
    minWidth: 220,
    maxWidth: 300,
    m: 1,
    p: 2,
    boxShadow: 3,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: 'background.paper',
};

type ConfigInputState = {
    enabled: boolean;
    rolloutPercent: string;
    batchSize: string;
    maxAttempts: string;
    delaySeconds: string;
};

const aiModerationNumberLimits = {
    rolloutPercent: { min: 0, max: 100, label: 'Rollout, %' },
    batchSize: { min: 1, max: 500, label: 'Размер батча' },
    maxAttempts: { min: 1, max: 10, label: 'Максимум попыток' },
    delaySeconds: { min: 1, max: 3600, label: 'Задержка, сек.' }
} as const;

type NumberConfigKey = keyof typeof aiModerationNumberLimits;
type ConfigFieldKey = keyof SetAiModerationConfigRequest;

const configFieldLabels: Record<ConfigFieldKey, string> = {
    enabled: 'AI модерация',
    rolloutPercent: 'Rollout',
    batchSize: 'Размер батча',
    maxAttempts: 'Максимум попыток',
    delaySeconds: 'Задержка'
};

function formatDateTime(value: string | null | undefined) {
    if (!value) {
        return 'не обновлялось';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'неизвестно';
    }
    return date.toLocaleString('ru-RU');
}

function formatConfigValue(field: ConfigFieldKey, value: boolean | number | null | undefined) {
    if (value === null || typeof value === 'undefined') {
        return 'не задано';
    }
    if (field === 'enabled') {
        return value ? 'включена' : 'выключена';
    }
    if (field === 'rolloutPercent') {
        return `${value}%`;
    }
    if (field === 'delaySeconds') {
        return `${value} сек.`;
    }
    return String(value);
}

function formatActorLabel(actor: AiModerationConfigHistoryItem['actor']) {
    const parts = [actor?.fullName, actor?.login].filter((value) => typeof value === 'string' && value.trim());
    return parts.length ? parts.join(' / ') : 'Неизвестный пользователь';
}

function configToInputState(config: AiModerationConfig): ConfigInputState {
    return {
        enabled: Boolean(config.enabled),
        rolloutPercent: String(config.rolloutPercent),
        batchSize: String(config.batchSize),
        maxAttempts: String(config.maxAttempts),
        delaySeconds: String(config.delaySeconds)
    };
}

function parseIntegerInput(value: string, field: NumberConfigKey) {
    const limits = aiModerationNumberLimits[field];
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedValue) {
        throw new Error(`${limits.label}: укажите значение`);
    }
    if (!/^-?\d+$/.test(normalizedValue)) {
        throw new Error(`${limits.label}: значение должно быть целым числом`);
    }
    const parsedValue = Number(normalizedValue);
    if (!Number.isSafeInteger(parsedValue) || parsedValue < limits.min || parsedValue > limits.max) {
        throw new Error(`${limits.label}: значение должно быть от ${limits.min} до ${limits.max}`);
    }
    return parsedValue;
}

function buildConfigPatch(sourceConfig: AiModerationConfig, inputState: ConfigInputState): SetAiModerationConfigRequest {
    const parsedValues = {
        rolloutPercent: parseIntegerInput(inputState.rolloutPercent, 'rolloutPercent'),
        batchSize: parseIntegerInput(inputState.batchSize, 'batchSize'),
        maxAttempts: parseIntegerInput(inputState.maxAttempts, 'maxAttempts'),
        delaySeconds: parseIntegerInput(inputState.delaySeconds, 'delaySeconds')
    };
    const patch: SetAiModerationConfigRequest = {};

    if (inputState.enabled !== sourceConfig.enabled) {
        patch.enabled = inputState.enabled;
    }

    (Object.keys(parsedValues) as NumberConfigKey[]).forEach((field) => {
        if (parsedValues[field] !== sourceConfig[field]) {
            patch[field] = parsedValues[field];
        }
    });

    return patch;
}

export const ProjectSettingsPage = () => {
    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto', width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Проект
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ width: 300 }}>
                    <Card sx={cardStyle}>
                        <SmartToyIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="h6" sx={{ mb: 1, textAlign: 'left', width: '100%' }}>
                            Настройка AI модерации
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', width: '100%' }}>
                            Параметры AI-проверки объявлений
                        </Typography>
                        <Button component={Link} to="/project-settings/ai-moderation" variant="contained" fullWidth>
                            Перейти
                        </Button>
                    </Card>
                </Box>
            </Stack>
        </Box>
    );
};

export const AiModerationSettingsPage = () => {
    const notify = useNotify();
    const { identity, isPending } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const isManagerAdmin = currentUser?.role === 'manager-admin';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [config, setConfig] = useState<AiModerationConfig | null>(null);
    const [history, setHistory] = useState<AiModerationConfigHistoryItem[]>([]);
    const [inputState, setInputState] = useState<ConfigInputState>({
        enabled: false,
        rolloutPercent: '',
        batchSize: '',
        maxAttempts: '',
        delaySeconds: ''
    });

    const loadConfig = useCallback(async () => {
        if (!isManagerAdmin) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setLoadError('');
        try {
            const [response, historyResponse] = await Promise.all([
                aiModerationConfigProxy.getConfig(),
                aiModerationConfigProxy.getHistory(20)
            ]);
            setConfig(response);
            setInputState(configToInputState(response));
            setHistory(Array.isArray(historyResponse?.data) ? historyResponse.data : []);
        } catch (error) {
            console.error('Failed to load AI moderation config:', error);
            setLoadError('Не удалось загрузить настройки AI модерации');
            notify('Не удалось загрузить настройки AI модерации', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [isManagerAdmin, notify]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const validationErrors = useMemo(() => {
        const result: Partial<Record<NumberConfigKey, string>> = {};
        (Object.keys(aiModerationNumberLimits) as NumberConfigKey[]).forEach((field) => {
            try {
                parseIntegerInput(inputState[field], field);
            } catch (error) {
                result[field] = error instanceof Error ? error.message : 'Некорректное значение';
            }
        });
        return result;
    }, [inputState]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    const handleNumberChange = (field: NumberConfigKey, value: string) => {
        setInputState((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        if (!config || saving) {
            return;
        }

        let patch: SetAiModerationConfigRequest;
        try {
            patch = buildConfigPatch(config, inputState);
        } catch (error) {
            const message = error instanceof Error && error.message.trim()
                ? error.message
                : 'Проверьте значения настроек';
            notify(message, { type: 'warning' });
            return;
        }

        if (Object.keys(patch).length === 0) {
            notify('Нет изменений', { type: 'info' });
            return;
        }

        setSaving(true);
        try {
            const response = await aiModerationConfigProxy.updateConfig(patch);
            const historyResponse = await aiModerationConfigProxy.getHistory(20);
            setConfig(response);
            setInputState(configToInputState(response));
            setHistory(Array.isArray(historyResponse?.data) ? historyResponse.data : []);
            notify('Настройки AI модерации сохранены', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error && error.message.trim()
                ? error.message
                : 'Не удалось сохранить настройки AI модерации';
            notify(message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (isPending || loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Настройка AI модерации" />
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (!isManagerAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Настройка AI модерации" />
                <Alert severity="error">Доступ только для роли manager-admin</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1180, mx: 'auto', width: '100%' }}>
            <Title title="Настройка AI модерации" />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2, gap: 1.5 }}>
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="h4">Настройка AI модерации</Typography>
                        <Chip
                            label={inputState.enabled ? 'Включена' : 'Выключена'}
                            color={inputState.enabled ? 'success' : 'default'}
                            variant={inputState.enabled ? 'filled' : 'outlined'}
                            size="small"
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Управление rollout, очередью и ретраями AI-проверки объявлений.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || !config || hasValidationErrors}
                >
                    {saving ? 'Сохраняю...' : 'Сохранить'}
                </Button>
            </Stack>

            {loadError ? (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={loadConfig}>
                            Повторить
                        </Button>
                    )}
                    sx={{ mb: 2 }}
                >
                    {loadError}
                </Alert>
            ) : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
                    gap: 2.5,
                    alignItems: 'start'
                }}
            >
                <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Основные параметры
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Изменения сохраняются одним PATCH-запросом только по измененным полям.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: inputState.enabled ? 'rgba(46, 125, 50, 0.12)' : 'action.hover',
                                    border: '1px solid',
                                    borderColor: inputState.enabled ? 'success.main' : 'divider'
                                }}
                            >
                                <FormControlLabel
                                    sx={{ m: 0, alignItems: 'center' }}
                                    control={(
                                        <Switch
                                            color="success"
                                            checked={inputState.enabled}
                                            onChange={(event) => setInputState((prev) => ({
                                                ...prev,
                                                enabled: event.target.checked
                                            }))}
                                            sx={{ mr: 1 }}
                                        />
                                    )}
                                    label={(
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                {inputState.enabled ? 'AI модерация включена' : 'AI модерация выключена'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Управляет запуском автоматической проверки объявлений.
                                            </Typography>
                                        </Box>
                                    )}
                                />
                            </Box>

                            <Divider />

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                    gap: 2
                                }}
                            >
                                <TextField
                                    label="Rollout, %"
                                    type="number"
                                    value={inputState.rolloutPercent}
                                    onChange={(event) => handleNumberChange('rolloutPercent', event.target.value)}
                                    error={Boolean(validationErrors.rolloutPercent)}
                                    helperText={validationErrors.rolloutPercent || 'Доля объявлений, отправляемых на AI-проверку. От 0 до 100.'}
                                    slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
                                    fullWidth
                                />
                                <TextField
                                    label="Размер батча"
                                    type="number"
                                    value={inputState.batchSize}
                                    onChange={(event) => handleNumberChange('batchSize', event.target.value)}
                                    error={Boolean(validationErrors.batchSize)}
                                    helperText={validationErrors.batchSize || 'Сколько объявлений обрабатывать за один проход. От 1 до 500.'}
                                    slotProps={{ htmlInput: { min: 1, max: 500, step: 1 } }}
                                    fullWidth
                                />
                                <TextField
                                    label="Максимум попыток"
                                    type="number"
                                    value={inputState.maxAttempts}
                                    onChange={(event) => handleNumberChange('maxAttempts', event.target.value)}
                                    error={Boolean(validationErrors.maxAttempts)}
                                    helperText={validationErrors.maxAttempts || 'Количество повторов при ошибках. От 1 до 10.'}
                                    slotProps={{ htmlInput: { min: 1, max: 10, step: 1 } }}
                                    fullWidth
                                />
                                <TextField
                                    label="Задержка, сек."
                                    type="number"
                                    value={inputState.delaySeconds}
                                    onChange={(event) => handleNumberChange('delaySeconds', event.target.value)}
                                    error={Boolean(validationErrors.delaySeconds)}
                                    helperText={validationErrors.delaySeconds || 'Пауза между попытками. От 1 до 3600.'}
                                    slotProps={{ htmlInput: { min: 1, max: 3600, step: 1 } }}
                                    fullWidth
                                />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
                    <CardContent sx={{ p: 0 }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                История изменений
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Последние сохранения параметров
                            </Typography>
                        </Box>
                        <Divider />
                        {history.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                                <Alert severity="info">История пока пустая.</Alert>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {history.map((item, index) => {
                                    const changes = Object.entries(item.changedFields || {}) as Array<[ConfigFieldKey, { before: boolean | number | null; after: boolean | number | null }]>;
                                    return (
                                        <Box key={item.id || `${item.createdAt}-${index}`}>
                                            {index > 0 ? <Divider /> : null}
                                            <ListItem alignItems="flex-start" sx={{ px: 2, py: 1.75 }}>
                                                <ListItemText
                                                    primary={(
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                {formatDateTime(item.createdAt)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatActorLabel(item.actor)}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                    secondary={(
                                                        <Stack spacing={0.75} sx={{ mt: 1 }}>
                                                            {changes.length === 0 ? (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Изменения не указаны
                                                                </Typography>
                                                            ) : changes.map(([field, change]) => (
                                                                <Box key={field}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                                        {configFieldLabels[field]}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {formatConfigValue(field, change.before)} {'->'} {formatConfigValue(field, change.after)}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    )}
                                                />
                                            </ListItem>
                                        </Box>
                                    );
                                })}
                            </List>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
